"use client";

import React from "react";
import { ArrowRight, Phone, ShieldCheck, Building2, House as Home, Hammer, Compass } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const accessTypes = [{
  icon: Building2,
  title: "Commercial",
  description: "Main contractor support, site management & scheduled phases"
}, {
  icon: Home,
  title: "Residential",
  description: "Extensions, private restorations & external refurbishments"
}, {
  icon: Hammer,
  title: "Roofing",
  description: "Chimney stacks, tile replacements & temporary roof covers"
}, {
  icon: Compass,
  title: "Specialist access",
  description: "Bespoke cantilevers, tight-access plots & heritage works"
}];
const accreditations = ["NASC Member", "CHAS Accredited"];
export default function ReadingHero() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="readingscaffoldinghero" data-nav="dark" className="relative overflow-hidden bg-secondary text-secondary-foreground py-20 lg:py-28">
      {/* Decorative backdrop elements */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 right-0 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-20 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(5,32,250,0.15),rgba(255,255,255,0))]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
          {/* Main Hero Content */}
          <div className="lg:col-span-7">
            <motion.div initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: "easeOut"
          }} className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="bg-primary/20 text-primary-foreground border-primary/30 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                Reading & Berkshire Access
              </Badge>
              <span className="text-xs text-secondary-foreground/70">
                Safety-led scaffolding contractor
              </span>
            </motion.div>

            <motion.h1 initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1,
            ease: "easeOut"
          }} className="mt-6 font-default text-4xl font-bold tracking-tight text-secondary-foreground sm:text-5xl lg:text-6xl">
              Scaffolding in Reading
            </motion.h1>

            <motion.p initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2,
            ease: "easeOut"
          }} className="mt-6 max-w-2xl text-lg leading-relaxed text-secondary-foreground/80 sm:text-xl">
              Safe, fully compliant access for commercial sites, residential builds, roofing contracts, and specialist projects across Reading and Berkshire.
            </motion.p>

            {/* Action Buttons */}
            <motion.div initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3,
            ease: "easeOut"
          }} className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
              <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-semibold" asChild>
                <Link to="/ContactRequestQuotePage">
                  Request a quote
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button size="lg" variant="outline" className="border-sidebar-border bg-sidebar/50 text-sidebar-foreground hover:bg-sidebar/80 hover:text-sidebar-foreground shadow-xs font-semibold" asChild>
                <a href="tel:01344929900" className="text-sidebar-foreground hover:text-sidebar-foreground">
                  <Phone className="size-4" />
                  Call 01344 929900
                </a>
              </Button>
            </motion.div>

            {/* Verified Accreditations */}
            <motion.div initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.4,
            ease: "easeOut"
          }} className="mt-10 border-t border-sidebar-border/60 pt-6">
              <p className="text-xs font-semibold tracking-wider text-secondary-foreground/60 uppercase">
                Industry Accreditations
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-y-2 gap-x-6">
                {accreditations.map((item, i) => <div key={item} data-index={i} className="flex items-center gap-2 text-sm font-medium text-secondary-foreground/90">
                    <ShieldCheck className="size-4 text-primary" />
                    <span>{item}</span>
                  </div>)}
              </div>
            </motion.div>
          </div>

          {/* Core Access Categories Panel */}
          <div className="lg:col-span-5">
            <motion.div initial={shouldReduceMotion ? false : {
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.2,
            ease: "easeOut"
          }} className="rounded-2xl border border-sidebar-border bg-sidebar/70 p-6 shadow-xl backdrop-blur-sm sm:p-8">
              <div className="mb-6 flex items-center justify-between border-b border-sidebar-border pb-4">
                <span className="font-default text-lg font-semibold text-sidebar-foreground">
                  Access Services in Reading
                </span>
                <span className="rounded-full bg-primary/20 px-2.5 py-0.5 text-xs font-semibold text-primary-foreground">
                  20+ Yrs Exp
                </span>
              </div>

              <div className="grid grid-cols-1 gap-4">
                {accessTypes.map((item, i) => {
                const Icon = item.icon;
                return <div key={item.title} data-index={i} className="group flex items-start gap-4 rounded-xl border border-sidebar-border/50 bg-secondary/50 p-4 transition-[background-color,border-color] duration-200 hover:border-primary/50 hover:bg-secondary/80">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/20 text-primary-foreground">
                        <Icon className="size-5" />
                      </div>
                      <div>
                        <h3 className="font-default text-base font-semibold text-secondary-foreground">
                          {item.title}
                        </h3>
                        <p className="mt-1 text-xs leading-normal text-secondary-foreground/70">
                          {item.description}
                        </p>
                      </div>
                    </div>;
              })}
              </div>

              <div className="mt-6 rounded-lg bg-sidebar-accent/50 p-3.5 text-center text-xs text-sidebar-foreground/80">
                Operating with fully certified CITB & CISRS trained scaffolders across Berkshire.
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}